import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/DashboardLayout";

function VideoLectures() {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    // Load videos from manifest file
    const loadVideos = async () => {
      try {
        const response = await fetch("/videos/videos.json");
        if (!response.ok) {
          throw new Error("Failed to load videos manifest");
        }
        const data = await response.json();

        // Filter only MP4 files and add full path
        const videoList = (data.videos || [])
          .filter((video) => {
            const filename = typeof video === "string" ? video : video.filename;
            return filename.toLowerCase().endsWith(".mp4");
          })
          .map((video) => {
            const filename = typeof video === "string" ? video : video.filename;
            const title =
              typeof video === "object" && video.title
                ? video.title
                : filename.replace(".mp4", "").replace(/_/g, " ");
            return {
              filename,
              title,
              path: `/videos/${filename}`,
            };
          });

        setVideos(videoList);
      } catch (err) {
        console.error("Error loading videos:", err);
        setError(t("videoLectures.errors.loadError"));
      } finally {
        setLoading(false);
      }
    };

    loadVideos();
  }, [t]);

  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("videoLectures.title")}
          </h1>
          <p className="text-gray-600 mt-1">{t("videoLectures.subtitle")}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Videos Grid */}
        {videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 bg-white rounded-lg border border-gray-200">
            <svg
              className="w-16 h-16 mb-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <p className="text-lg font-medium mb-2">
              {t("videoLectures.noVideos")}
            </p>
            <p className="text-sm text-gray-500 text-center max-w-md">
              {t("videoLectures.noVideosDescription")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {videos.map((video, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-black hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
                onClick={() => handleVideoSelect(video)}
              >
                {/* Video Thumbnail/Preview */}
                <div className="relative aspect-video bg-gray-900 flex items-center justify-center overflow-hidden">
                  <video
                    src={video.path}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    preload="metadata"
                    muted
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300">
                    <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-2xl transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <svg
                        className="w-10 h-10 text-black ml-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>

                  {/* Duration Badge (if available) */}
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-medium px-2 py-1 rounded backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <svg
                      className="w-4 h-4 inline mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Video
                  </div>
                </div>

                {/* Video Title */}
                <div className="p-5 bg-gradient-to-b from-white to-gray-50">
                  <h3 className="font-bold text-gray-900 line-clamp-2 text-lg group-hover:text-black transition-colors">
                    {video.title}
                  </h3>
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    {t("videoLectures.lecture")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Video Player Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
            <div className="w-full h-full flex flex-col relative">
              {/* Close Button */}
              <button
                onClick={handleCloseVideo}
                className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors bg-black/50 hover:bg-black/70 rounded-full p-2 backdrop-blur-sm"
                aria-label={t("videoLectures.close")}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Video Title */}
              <div className="absolute top-4 left-4 z-10 max-w-2xl">
                <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                  {selectedVideo.title}
                </h3>
              </div>

              {/* Video Player */}
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="relative w-full max-w-7xl aspect-video bg-black">
                  <video
                    src={selectedVideo.path}
                    controls
                    autoPlay
                    className="w-full h-full"
                  >
                    {t("videoLectures.videoNotSupported")}
                  </video>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default VideoLectures;
